/**
 * HcTimer Model
 *
 * Centralized timeout management for health check system.
 * Used to track various types of timeouts including:
 * - Notification response timeouts
 * - Webhook response timeouts
 * - Call timeouts
 *
 * A cron job processes expired timers and triggers appropriate cleanup actions.
 */

export interface HcTimer {
  _id: string;

  // Timer classification
  type: HcTimerType;
  referenceId: string; // ID of the related entity (notification attempt, active call, etc.)
  referenceType: HcTimerReferenceType;

  // Timeout configuration
  timeoutAt: Date; // When this timer will expire
  timeoutMinutes: number; // Original timeout duration for reference

  // Timer state
  status: HcTimerStatus;

  // Context and metadata
  metadata?: HcTimerMetadata; // Additional context for timeout handling

  // Timestamps
  createdAt: Date;
  completedAt?: Date; // When timer was completed (response received or manually cancelled)
  processedAt?: Date; // When timeout was processed by cron job

  // Error handling
  errorMessage?: string; // Error if timeout processing failed
  retryCount?: number; // Number of retry attempts for processing
  maxRetries?: number; // Maximum retry attempts (default: 3)
}

export enum HcTimerType {
  NOTIFICATION_RESPONSE = 'HcTimerType.notification_response', // Waiting for SMS/voice response
  WEBHOOK_RESPONSE = 'HcTimerType.webhook_response', // Waiting for Twilio webhook
  CALL_TIMEOUT = 'HcTimerType.call_timeout', // Overall call timeout
  NOTIFICATION_DELIVERY = 'HcTimerType.notification_delivery' // Waiting for delivery confirmation
}

export enum HcTimerReferenceType {
  NOTIFICATION_ATTEMPT = 'HcTimerReferenceType.notification_attempt',
  ACTIVE_CALL = 'HcTimerReferenceType.active_call',
  WEBHOOK_EXPECTED = 'HcTimerReferenceType.webhook_expected',
  HISTORY_RECORD = 'HcTimerReferenceType.history_record'
}

export enum HcTimerStatus {
  ACTIVE = 'HcTimerStatus.active', // Timer is active and waiting
  PROCESSING = 'HcTimerStatus.processing', // Currently being processed by cron job
  FAILED = 'HcTimerStatus.failed' // Processing failed after max retries
  // Note: COMPLETED, TIMED_OUT, and CANCELLED timers are deleted immediately
}

export interface HcTimerMetadata {
  // For notification response timers
  contactId?: string;
  communicationMethod?: string;
  phoneNumber?: string;
  languageCode?: string;

  // For webhook response timers
  twilioCallSid?: string;
  expectedWebhookType?: string;

  // For call timeout timers
  flowId?: string;
  csId?: string;
  lovedOneId?: string;

  // General context
  originalResponseMeaning?: string;
  retryAttempt?: number;
  parentTimerId?: string; // For chained timers

  // Custom data
  [key: string]: any;
}

/**
 * Utility functions for HcTimer
 */
export class HcTimerUtil {
  /**
   * Creates a new timer for notification response
   */
  static createNotificationResponseTimer(
    notificationAttemptId: string,
    timeoutMinutes: number,
    metadata: HcTimerMetadata
  ): Partial<HcTimer> {
    const now = new Date();
    return {
      type: HcTimerType.NOTIFICATION_RESPONSE,
      referenceId: notificationAttemptId,
      referenceType: HcTimerReferenceType.NOTIFICATION_ATTEMPT,
      timeoutAt: new Date(now.getTime() + timeoutMinutes * 60 * 1000),
      timeoutMinutes,
      status: HcTimerStatus.ACTIVE,
      metadata,
      createdAt: now,
      retryCount: 0,
      maxRetries: 3
    };
  }

  /**
   * Creates a new timer for webhook response
   */
  static createWebhookResponseTimer(
    activeCallId: string,
    timeoutMinutes: number,
    metadata: HcTimerMetadata
  ): Partial<HcTimer> {
    const now = new Date();
    return {
      type: HcTimerType.WEBHOOK_RESPONSE,
      referenceId: activeCallId,
      referenceType: HcTimerReferenceType.ACTIVE_CALL,
      timeoutAt: new Date(now.getTime() + timeoutMinutes * 60 * 1000),
      timeoutMinutes,
      status: HcTimerStatus.ACTIVE,
      metadata,
      createdAt: now,
      retryCount: 0,
      maxRetries: 3
    };
  }

  /**
   * Creates a new timer for call timeout
   */
  static createCallTimeoutTimer(
    activeCallId: string,
    timeoutMinutes: number,
    metadata: HcTimerMetadata
  ): Partial<HcTimer> {
    const now = new Date();
    return {
      type: HcTimerType.CALL_TIMEOUT,
      referenceId: activeCallId,
      referenceType: HcTimerReferenceType.ACTIVE_CALL,
      timeoutAt: new Date(now.getTime() + timeoutMinutes * 60 * 1000),
      timeoutMinutes,
      status: HcTimerStatus.ACTIVE,
      metadata,
      createdAt: now,
      retryCount: 0,
      maxRetries: 3
    };
  }

  /**
   * Checks if a timer has expired
   */
  static isExpired(timer: HcTimer): boolean {
    return timer.status === HcTimerStatus.ACTIVE && new Date() > timer.timeoutAt;
  }

  /**
   * Checks if a timer is ready for processing
   */
  static isReadyForProcessing(timer: HcTimer): boolean {
    return timer.status === HcTimerStatus.ACTIVE &&
           this.isExpired(timer) &&
           (timer.retryCount || 0) < (timer.maxRetries || 3);
  }

  /**
   * Generates a unique timer ID
   */
  static generateTimerId(): string {
    return `timer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
